/**
 * Shared Bézier / curve sampling seam (P4).
 *
 * Document ops, tessellation, and hit-test all go through these helpers so
 * adaptive steps and cubic samples stay consistent. Production prefers the
 * Wasm backend registered by `enableWasmMeshKernels`; TS is the soft-fail default.
 */

export interface Point {
  x: number;
  y: number;
}

export const VECTOR_CURVE_TOLERANCE = 0.01;

export interface CurveKernelBackend {
  sampleEdgeBezier(
    from: Point,
    to: Point,
    fromHandle: Point | undefined,
    toHandle: Point | undefined,
    steps: number,
  ): Point[];
  adaptiveCurveSteps(
    from: Point,
    to: Point,
    fromHandle: Point | undefined,
    toHandle: Point | undefined,
  ): number;
}

function distancePointToLine(point: Point, from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length <= 0.0001) {
    return Math.hypot(point.x - from.x, point.y - from.y);
  }
  return Math.abs((point.x - from.x) * dy - (point.y - from.y) * dx) / length;
}

function evaluateCubic(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
    y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y,
  };
}

function sampleCubic(p0: Point, p1: Point, p2: Point, p3: Point, steps: number): Point[] {
  const safeSteps = Math.max(1, Math.floor(steps));
  return Array.from({ length: safeSteps + 1 }, (_, index) => {
    return evaluateCubic(p0, p1, p2, p3, index / safeSteps);
  });
}

function tsSampleEdgeBezier(
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
  steps: number,
): Point[] {
  if (!fromHandle && !toHandle) {
    return [from, to];
  }
  return sampleCubic(from, fromHandle ?? from, toHandle ?? to, to, steps);
}

function tsAdaptiveCurveSteps(
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
): number {
  if (!fromHandle && !toHandle) {
    return 1;
  }

  const flatness = Math.max(
    fromHandle ? distancePointToLine(fromHandle, from, to) : 0,
    toHandle ? distancePointToLine(toHandle, from, to) : 0,
  );
  if (flatness <= VECTOR_CURVE_TOLERANCE) {
    return 1;
  }

  return Math.min(64, Math.max(2, Math.ceil(Math.sqrt(flatness / VECTOR_CURVE_TOLERANCE))));
}

const tsBackend: CurveKernelBackend = {
  sampleEdgeBezier: tsSampleEdgeBezier,
  adaptiveCurveSteps: tsAdaptiveCurveSteps,
};

let backend: CurveKernelBackend = tsBackend;

export function setCurveKernelBackend(next: CurveKernelBackend): void {
  backend = next;
}

export function resetCurveKernelBackendForTests(): void {
  backend = tsBackend;
}

export function getCurveKernelBackend(): CurveKernelBackend {
  return backend;
}

export function sampleEdgeBezier(
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
  steps: number,
): Point[] {
  return backend.sampleEdgeBezier(from, to, fromHandle, toHandle, steps);
}

export function adaptiveCurveSteps(
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
): number {
  return backend.adaptiveCurveSteps(from, to, fromHandle, toHandle);
}

/** Unpack Wasm flat xy `Float32Array` into `{x,y}[]`. */
export function unpackFlatPoints(flat: Float32Array | number[]): Point[] {
  const points: Point[] = [];
  for (let index = 0; index + 1 < flat.length; index += 2) {
    points.push({ x: flat[index]!, y: flat[index + 1]! });
  }
  return points;
}

export function handleCoord(handle: Point | undefined, axis: "x" | "y"): number {
  return handle ? handle[axis] : Number.NaN;
}
